import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Alert, Button, Container } from '@mui/material'
import { FullPageLoader } from '@/components/common/FullPageLoader'
import { useAuth } from './useAuth'

/**
 * Gate a route behind an authenticated session.
 *
 * Waits for the initial `/auth/me` check before deciding, so a valid stored
 * token does not flash the sign-in page on a hard refresh.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, isError, reload } = useAuth()
  const location = useLocation()

  if (isLoading) return <FullPageLoader label="Loading" />

  if (isError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void reload()}>
              Retry
            </Button>
          }
        >
          Could not restore your session. Check that the API is running, then try again.
        </Alert>
      </Container>
    )
  }

  if (!isAuthenticated) return <Navigate to="/signin" replace state={{ from: location }} />

  return <>{children}</>
}
